// apps/client/src/world/terrain/terraces.ts — one bevelled extrusion per height tier: meadow top over a warm clay side (DESIGN.md "toy island")
import { tuning } from '@whiskerton/content';
import type { MapData } from '@whiskerton/shared';
import * as THREE from 'three';
import { contourShapes, tierField } from './field';
import { meadowTexture } from './textures';

/** DESIGN.md terrace-k tops and clays, k = 1…3 (grass lightens as it climbs; clay stays warm tan): tuning.light.ground. */
export const TERRACE_TOP = ['', ...tuning.light.ground.top] as const;
export const TERRACE_CLAY = ['', ...tuning.light.ground.clay] as const;
/** The world shader bends each vertex down with its distance from the camera, so a flat top must not have long triangles (an
 *  80-tile triangle's middle would sag by ~3.5 units): caps are cut along this grid, which keeps the sag under 0.03. */
export const CAP_GRID = 4;

/** [grass top, clay side] of terrace k. The top reads the ground map (paths, patches, shade, lamp pools: world/terrain/ground.ts). */
export function terraceMaterials(k: 1 | 2 | 3): [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial] {
  const top = new THREE.MeshStandardMaterial({ map: meadowTexture(TERRACE_TOP[k]), roughness: 0.95, metalness: 0 });
  top.userData.ground = true;
  return [top, new THREE.MeshStandardMaterial({ color: TERRACE_CLAY[k], roughness: 0.95, metalness: 0 })];
}

type P = [number, number];   // (x, z)
/** Where segment a–b crosses coordinate `axis` = v, computed from the canonically ordered endpoints so the two triangles that share
 *  an edge get bit-identical points (no cracks once the shader bends the vertices). */
function crossing(a: P, b: P, axis: 0 | 1, v: number): P {
  const [p, q] = a[0] < b[0] || (a[0] === b[0] && a[1] <= b[1]) ? [a, b] : [b, a];
  const t = (v - p[axis]) / (q[axis] - p[axis]), o = (1 - axis) as 0 | 1;
  const out: P = [0, 0];
  out[axis] = v;
  out[o] = p[o] + t * (q[o] - p[o]);
  return out;
}
/** Splits a convex polygon by the line coordinate[axis] = v (Sutherland–Hodgman); winding is kept. */
function split(poly: P[], axis: 0 | 1, v: number): [P[], P[]] {
  const lo: P[] = [], hi: P[] = [];
  poly.forEach((a, i) => {
    const b = poly[(i + 1) % poly.length]!, da = a[axis] - v, db = b[axis] - v;
    if (da <= 0) lo.push(a);
    if (da >= 0) hi.push(a);
    if ((da < 0 && db > 0) || (da > 0 && db < 0)) { const c = crossing(a, b, axis, v); lo.push(c); hi.push(c); }
  });
  return [lo, hi];
}
/** Cuts a triangle along every grid line it crosses and fans the convex pieces back into triangles. */
export function sliceTriangle(tri: readonly [P, P, P], grid: number): [P, P, P][] {
  let pieces: P[][] = [[...tri]];
  for (const axis of [0, 1] as const) {
    const next: P[][] = [];
    for (const poly of pieces) {
      const vs = poly.map((p) => p[axis]), lo = Math.floor(Math.min(...vs) / grid) + 1, hi = Math.ceil(Math.max(...vs) / grid) - 1;
      let rest = poly;
      for (let k = lo; k <= hi; k++) { const [a, b] = split(rest, axis, k * grid); if (a.length >= 3) next.push(a); rest = b; }
      if (rest.length >= 3) next.push(rest);
    }
    pieces = next;
  }
  const out: [P, P, P][] = [];
  for (const poly of pieces) for (let i = 1; i + 1 < poly.length; i++) {
    const t: [P, P, P] = [poly[0]!, poly[i]!, poly[i + 1]!];
    if (Math.abs((t[1][0] - t[0][0]) * (t[2][1] - t[0][1]) - (t[2][0] - t[0][0]) * (t[1][1] - t[0][1])) > 1e-9) out.push(t);
  }
  return out;
}

/** Rebuilds an extrusion: the top cap cut along CAP_GRID, the hidden bottom cap dropped, the sides as they are. */
function tessellate(geo: THREE.BufferGeometry, top: number): THREE.BufferGeometry {
  const pos = geo.attributes.position!, nor = geo.attributes.normal!, uv = geo.attributes.uv!;
  const capPos: number[] = [], sidePos: number[] = [], sideNor: number[] = [], sideUv: number[] = [];
  for (const g of geo.groups) for (let i = g.start; i < g.start + g.count; i += 3) {
    if (g.materialIndex === 0) {
      if (Math.abs(pos.getY(i) - top) > 1e-4) continue;                             // the bottom cap
      const tri: [P, P, P] = [[pos.getX(i), pos.getZ(i)], [pos.getX(i + 1), pos.getZ(i + 1)], [pos.getX(i + 2), pos.getZ(i + 2)]];
      for (const t of sliceTriangle(tri, CAP_GRID)) for (const [x, z] of t) capPos.push(x, top, z);
    } else for (let k = i; k < i + 3; k++) {
      sidePos.push(pos.getX(k), pos.getY(k), pos.getZ(k)); sideNor.push(nor.getX(k), nor.getY(k), nor.getZ(k)); sideUv.push(uv.getX(k), uv.getY(k));
    }
  }
  const nCap = capPos.length / 3, nSide = sidePos.length / 3;
  const out = new THREE.BufferGeometry();
  const capNor = new Float32Array(nCap * 3), capUv = new Float32Array(nCap * 2);
  for (let k = 0; k < nCap; k++) { capNor[k * 3 + 1] = 1; capUv[k * 2] = capPos[k * 3]!; capUv[k * 2 + 1] = -capPos[k * 3 + 2]!; }   // the caps' world UVs
  out.setAttribute('position', new THREE.BufferAttribute(new Float32Array([...capPos, ...sidePos]), 3));
  out.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([...capNor, ...sideNor]), 3));
  out.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([...capUv, ...sideUv]), 2));
  out.addGroup(0, nCap, 0);
  out.addGroup(nCap, nSide, 1);
  return out;
}

/** Tiers 1–3 as extrusions of their contour; tier 1 starts below the sea, each higher tier starts on the one below. */
export function buildTerraces(map: Pick<MapData, 'w' | 'h' | 'tiles'>): THREE.Group {
  const group = new THREE.Group();
  group.name = 'terraces';
  const rise = tuning.map.tierRise;
  for (const k of [1, 2, 3] as const) {
    const shapes = contourShapes(tierField(map, k), 0.5, { x0: -2, z0: -2, w: map.w + 4, h: map.h + 4 }, 0.25);
    if (shapes.length === 0) continue;
    const bottom = k === 1 ? -0.6 : (k - 1) * rise, top = k * rise, b = Math.min(0.14, (top - bottom) / 3);
    const geo = new THREE.ExtrudeGeometry(shapes, { depth: top - bottom - 2 * b, bevelEnabled: true, bevelThickness: b, bevelSize: b,
      bevelOffset: -b, bevelSegments: 3, steps: 1, curveSegments: 1 });
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, bottom + b, 0);
    const mesh = new THREE.Mesh(tessellate(geo, top), terraceMaterials(k));
    geo.dispose();
    mesh.name = `terrace${k}`;
    mesh.castShadow = mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}
